/**
 * HRMS Multi-Theme Registry
 *
 * Each entry defines a theme's metadata. The actual CSS variables live in
 * src/styles/theme.css under the [data-theme="<id>"] selector.
 *
 * To add a new theme:
 *   1. Add a record here.
 *   2. Add a [data-theme="<id>"] block in theme.css.
 *   That's it — no other file needs to change.
 */

export type ThemeId =
  | 'enterprise'
  | 'electemb'
  | 'light'
  | 'dark'
  | 'blue'
  | 'corporate'
  | 'ocean'
  | 'high-contrast';

export interface ThemeMeta {
  /** Matches the [data-theme] attribute value in CSS */
  id: ThemeId;
  /** Short human-readable name shown in the picker */
  label: string;
  /** Subtitle / purpose description */
  description: string;
  /** Determines icon set and contrast expectations */
  isDark: boolean;
  /** Hex of the primary brand color — used for swatch preview */
  primarySwatch: string;
  /** Hex of the main surface background — used for swatch preview */
  surfaceSwatch: string;
  /** Accessible text color on the surface swatch */
  swatchTextColor: string;
  /** Unicode emoji / icon for compact display */
  icon: string;
}

/** Ordered list of all available themes. */
export const THEMES: ThemeMeta[] = [
  {
    id: 'enterprise',
    label: 'Enterprise',
    description: 'Corporate navy · sky-blue accent',
    isDark: true,
    primarySwatch: '#60a5fa',
    surfaceSwatch: '#1B2B4B',
    swatchTextColor: '#e8edf5',
    icon: '🏢',
  },
  {
    id: 'electemb',
    label: 'ElectEmb',
    description: 'Original teal aesthetic · dark navy',
    isDark: true,
    primarySwatch: '#0DBFB0',
    surfaceSwatch: '#1B2B4B',
    swatchTextColor: '#e8edf5',
    icon: '🌿',
  },
  {
    id: 'light',
    label: 'Light',
    description: 'Clean white — legacy light mode',
    isDark: false,
    primarySwatch: '#0DBFB0',
    surfaceSwatch: '#FFFFFF',
    swatchTextColor: '#0F172A',
    icon: '☀️',
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'Dark slate for low-light environments',
    isDark: true,
    primarySwatch: '#0DBFB0',
    surfaceSwatch: '#1E293B',
    swatchTextColor: '#F1F5F9',
    icon: '🌙',
  },
  {
    id: 'blue',
    label: 'Blue',
    description: 'Professional sky-blue · dark navy',
    isDark: true,
    primarySwatch: '#38bdf8',
    surfaceSwatch: '#0d2137',
    swatchTextColor: '#e0f2fe',
    icon: '🔵',
  },
  {
    id: 'corporate',
    label: 'Corporate',
    description: 'Warm gold accent · deep navy',
    isDark: true,
    primarySwatch: '#c8a96e',
    surfaceSwatch: '#1a2744',
    swatchTextColor: '#f0ece4',
    icon: '🏛️',
  },
  {
    id: 'ocean',
    label: 'Ocean',
    description: 'Midnight depths · cyan accent',
    isDark: true,
    primarySwatch: '#06b6d4',
    surfaceSwatch: '#061526',
    swatchTextColor: '#cffafe',
    icon: '🌊',
  },
  {
    id: 'high-contrast',
    label: 'High Contrast',
    description: 'WCAG AAA · maximum accessibility',
    isDark: true,
    primarySwatch: '#FFFF00',
    surfaceSwatch: '#000000',
    swatchTextColor: '#FFFFFF',
    icon: '⚡',
  },
];

export const DEFAULT_THEME: ThemeId = 'light';

/** Lookup a theme by id, falling back to the default. */
export function getThemeMeta(id: string): ThemeMeta {
  return THEMES.find(t => t.id === id) ?? THEMES[0];
}
